import { useState } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { HrHierarchyNode } from '@/types/hr';
import { cn } from '@/utils/cn';

interface HrHierarchyTreeProps {
  nodes: HrHierarchyNode[];
  selectedId?: number;
  onSelect: (employeeId: number) => void;
  defaultExpanded?: boolean;
  className?: string;
}

function NodeRow({
  node,
  depth,
  selectedId,
  onSelect,
  defaultExpanded,
}: {
  node: HrHierarchyNode;
  depth: number;
  selectedId?: number;
  onSelect: (id: number) => void;
  defaultExpanded: boolean;
}) {
  const hasChildren = node.children.length > 0;
  const [open, setOpen] = useState(defaultExpanded || depth < 1);
  const selected = selectedId === node.employeeId;
  const initials = node.fullName
    .split(' ')
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div>
      <div
        className={cn(
          'flex items-center gap-2 rounded-lg px-2 py-2 text-sm transition-colors',
          selected
            ? 'bg-primary-50 text-primary-900 dark:bg-primary-950/50 dark:text-primary-100'
            : 'hover:bg-slate-50 dark:hover:bg-slate-800/60',
        )}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
      >
        {hasChildren ? (
          <button
            type="button"
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'Collapse' : 'Expand'}
          >
            {open ? <ExpandMoreIcon sx={{ fontSize: 18 }} /> : <ChevronRightIcon sx={{ fontSize: 18 }} />}
          </button>
        ) : (
          <span className="w-6 shrink-0" />
        )}
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          onClick={() => onSelect(node.employeeId)}
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-100">
            {initials}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium">{node.fullName}</span>
            <span className="block truncate text-xs text-slate-500">
              {node.designation ?? node.employeeCode}
              {node.departmentName ? ` · ${node.departmentName}` : ''}
              {hasChildren ? ` · ${node.directReportCount} reports` : ''}
            </span>
          </span>
        </button>
      </div>
      {hasChildren && open && (
        <div>
          {node.children.map((child) => (
            <NodeRow
              key={child.employeeId}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              onSelect={onSelect}
              defaultExpanded={defaultExpanded}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function HrHierarchyTree({
  nodes,
  selectedId,
  onSelect,
  defaultExpanded = false,
  className,
}: HrHierarchyTreeProps) {
  if (nodes.length === 0) {
    return <p className="px-2 py-4 text-sm text-slate-500">No reporting hierarchy found.</p>;
  }

  return (
    <div className={cn('space-y-0.5', className)}>
      {nodes.map((node) => (
        <NodeRow
          key={node.employeeId}
          node={node}
          depth={0}
          selectedId={selectedId}
          onSelect={onSelect}
          defaultExpanded={defaultExpanded}
        />
      ))}
    </div>
  );
}
